import { Currency } from '../types';
import i18next from 'i18next';

// Get currency symbol
export const getCurrencySymbol = (currency: Currency): string => {
  switch (currency) {
    case Currency.USD:
      return '$';
    case Currency.MXN:
      return '$';
    case Currency.EUR:
      return '€';
    case Currency.GBP:
      return '£';
    case Currency.JPY:
      return '¥';
    case Currency.CNY:
      return '¥';
    case Currency.RUB:
      return '₽';
    default:
      return '$';
  }
};

// Get translated currency name
export const getTranslatedCurrencyName = (currency: Currency): string => {
  return i18next.t(`common.currency.${currency}`) || currency;
};

// Currency conversion rates (as of 2025)
// All rates are relative to USD (our base currency)
const CONVERSION_RATES: Record<Currency, number> = {
  [Currency.USD]: 1.00,    // Base currency
  [Currency.MXN]: 18.50,   // 1 USD = 18.50 MXN (updated to a more realistic rate)
  [Currency.EUR]: 0.85,    // 1 USD = 0.85 EUR
  [Currency.GBP]: 0.73,    // 1 USD = 0.73 GBP
  [Currency.JPY]: 110.00,  // 1 USD = 110.00 JPY
  [Currency.CNY]: 6.45,    // 1 USD = 6.45 CNY
  [Currency.RUB]: 75.00    // 1 USD = 75.00 RUB
};

// Convert amount between currencies
export const convertCurrency = (
  amount: number,
  fromCurrency: Currency = Currency.USD, // Default from currency is USD
  toCurrency: Currency
): number => {
  // Validate inputs
  if (!amount || !fromCurrency || !toCurrency) {
    console.warn('Invalid parameters for currency conversion:', { amount, fromCurrency, toCurrency });
    return amount;
  }

  // If the currencies are the same, return the original amount
  if (fromCurrency === toCurrency) {
    return Math.ceil(amount);
  }

  try {
    if (fromCurrency === Currency.USD) {
      // Direct conversion from USD to target currency
      return Math.ceil(amount * CONVERSION_RATES[toCurrency]);
    } else if (toCurrency === Currency.USD) {
      // Convert from source currency to USD
      return Math.ceil(amount / CONVERSION_RATES[fromCurrency]);
    } else {
      // Convert from source to USD, then from USD to target
      const amountInUSD = amount / CONVERSION_RATES[fromCurrency];
      return Math.ceil(amountInUSD * CONVERSION_RATES[toCurrency]);
    }
  } catch (error) {
    console.error('Error converting currency:', error);
    return amount;
  }
};

// Format currency with proper symbol and formatting
export const formatCurrency = (amount: number, currency: Currency = Currency.MXN): string => {
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.ceil(amount));

  // For USD and MXN, symbol goes before the amount
  if (currency === Currency.USD || currency === Currency.MXN) {
    return `${symbol}${formattedAmount}`;
  }
  
  // For other currencies, follow their traditional format
  return `${formattedAmount}${symbol}`;
};